import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Toaster } from 'react-hot-toast';
import Header from './components/Header';
import Home from './pages/Home';
import TermsOfUse from './pages/TermsOfUse';
import PrivacyPolicy from './pages/PrivacyPolicy';
import NotFound from './pages/NotFound';
import './i18n';
import './App.css';

function App() {
  const [theme, setTheme] = useState('light');
  const { i18n } = useTranslation();

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    setTheme(savedTheme);
    document.documentElement.setAttribute('data-theme', savedTheme);
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
  };

  // will be provided to Header from inside the Router so it can navigate
  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
  };

  return (
    <Router>
      <div className="App">
        {/* RoutesWithLocale is inside Router so it can use navigation hooks */}
        <RoutesWithLocale
          theme={theme}
          toggleTheme={toggleTheme}
          changeLanguageProp={changeLanguage}
        />
        <Toaster position="top-right" />
      </div>
    </Router>
  );
}

export default App;

// Helper component that lives inside Router and handles optional locale prefix
function RoutesWithLocale({ theme, toggleTheme, changeLanguageProp }) {
  const supported = ['en', 'pt', 'es'];
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if we need to redirect routes without locale prefix
  useEffect(() => {
    const pathname = location.pathname;
    const parts = pathname.split('/').filter(Boolean);
    const first = parts[0];
    const supportedLangs = ['en', 'pt', 'es'];
    
    // If first part is not a supported language and we have a route that needs locale
    if (!supportedLangs.includes(first) && (pathname === '/terms' || pathname === '/privacy')) {
      const currentLang = i18n.language || 'pt'; // default to Portuguese
      const newPath = `/${currentLang}${pathname}`;
      navigate(newPath, { replace: true });
    }
  }, [location.pathname, navigate, i18n.language]);

  // changeLanguage that also updates the URL to include selected locale
  const changeLanguage = (lang) => {
    if (!supported.includes(lang)) return;
    const pathname = location.pathname;
    // strip leading locale if present
    const parts = pathname.split('/').filter(Boolean);
    const first = parts[0];
    let rest = '';
    if (supported.includes(first)) {
      rest = parts.slice(1).join('/');
    } else {
      rest = parts.join('/');
    }
    const newPath = `/${lang}` + (rest ? `/${rest}` : '');
    // preserve hash and search
    const search = location.search || '';
    const hash = location.hash || '';

    i18n.changeLanguage(lang);
    // navigate to the new path
    navigate(newPath + search + hash, { replace: true });
  };

  // component that syncs i18n to the :lng param when present
  function LocaleSync() {
    const params = useParams();
    const lng = params.lng;
    useEffect(() => {
      if (lng && supported.includes(lng) && i18n.language !== lng) {
        i18n.changeLanguage(lng);
      }
      // if lng is present but not supported, do nothing (could redirect)
    }, [lng]);

    return (
      <>
        <Header theme={theme} toggleTheme={toggleTheme} changeLanguage={changeLanguage} />
        <Routes>
          {/* When mounted under /:lng/* or /, these routes resolve relative to that base */}
          <Route index element={<Home />} />
          <Route path="terms" element={<TermsOfUse />} />
          <Route path="privacy" element={<PrivacyPolicy />} />
          {/* wildcard for unknown routes under the current base (will show 404) */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </>
    );
  }

  return (
    <Routes>
      {/* Route with explicit locale prefix */}
      <Route path=":lng/*" element={<LocaleSync />} />
      {/* Fallback routes without prefix */}
      <Route path="*" element={<LocaleSync />} />
    </Routes>
  );
}
